/**
 * DocumentsPage (Phase-2, Module 9)
 *
 * Officer medical document repository — upload across 10 categories, search,
 * filter, preview/download, version history, and expiry awareness.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, FormControl, InputLabel, Select, IconButton, Tooltip, Chip, Typography, Divider, Stack,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Folder as DocIcon, Upload as UploadIcon, Visibility as ViewIcon, History as HistoryIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload, ConfirmDialog, OfficerSelector } from '../../components/common';
import { MedicalDocumentApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { DOCUMENT_CATEGORIES, ACCESS_LEVELS, labelFor } from '../../utils/phase2Constants';
import type { MedicalDocument, MedicalDocumentRequest } from '../../types/phase2';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const DocumentsPage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = useState<MedicalDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState('');
  const [search, setSearch] = useState('');

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<MedicalDocumentRequest>({
    officerId: 0, documentTitle: '', documentCategory: 'PRESCRIPTION',
    hospitalName: '', doctorName: '', documentDate: new Date().toISOString().slice(0, 10),
    expiryDate: '', tags: '', description: '', accessLevel: 'PRIVATE',
  });

  const [detail, setDetail] = useState<MedicalDocument | null>(null);
  const [versions, setVersions] = useState<MedicalDocument[]>([]);
  const [verOpen, setVerOpen] = useState(false);
  const [verFile, setVerFile] = useState<File | null>(null);
  const [verTitle, setVerTitle] = useState('');
  const [uploading, setUploading] = useState(false);
  const [delId, setDelId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try {
      const page = await MedicalDocumentApi.listByOfficer(officerId);
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load documents'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { if (officerId) setForm((f) => ({ ...f, officerId })); }, [officerId]);
  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter((r) => {
    if (catFilter && r.documentCategory !== catFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.documentTitle?.toLowerCase().includes(q) || r.hospitalName?.toLowerCase().includes(q) || r.doctorName?.toLowerCase().includes(q);
    }
    return true;
  });

  const handleSave = async () => {
    if (!officerId || !file || !form.documentTitle) return;
    setSaving(true); setError(null);
    try {
      await MedicalDocumentApi.upload(file, {
        officerId, documentTitle: form.documentTitle, documentCategory: form.documentCategory,
        hospitalName: form.hospitalName || undefined, doctorName: form.doctorName || undefined,
        documentDate: form.documentDate || undefined, expiryDate: form.expiryDate || undefined,
        tags: form.tags || undefined, description: form.description || undefined,
        accessLevel: form.accessLevel,
      });
      setOpen(false); setFile(null);
      setForm({ officerId, documentTitle: '', documentCategory: 'PRESCRIPTION', hospitalName: '', doctorName: '', documentDate: new Date().toISOString().slice(0, 10), expiryDate: '', tags: '', description: '', accessLevel: 'PRIVATE' });
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Upload failed'); }
    finally { setSaving(false); }
  };

  const openVersions = async (row: MedicalDocument) => {
    setDetail(row);
    try {
      const full = await MedicalDocumentApi.getById(row.id);
      setVersions(full.versions || []);
    } catch { setVersions([]); }
    setVerOpen(true);
  };

  const handleAddVersion = async () => {
    if (!detail || !verFile) return;
    setUploading(true); setError(null);
    try {
      await MedicalDocumentApi.createVersion(detail.id, verFile, { documentTitle: verTitle || undefined });
      const full = await MedicalDocumentApi.getById(detail.id);
      setVersions(full.versions || []); setDetail(full);
      setVerFile(null); setVerTitle('');
      load();
    } catch (e: any) { setError(e?.response?.data?.message || 'Version upload failed'); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    if (delId == null) return;
    try {
      await MedicalDocumentApi.delete(delId);
      setDelId(null);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Delete failed');
      setDelId(null);
    }
  };

  const columns: GridColDef[] = [
    { field: 'documentTitle', headerName: 'Title', flex: 1.4, minWidth: 180 },
    { field: 'documentCategory', headerName: 'Category', width: 150, renderCell: (p) => <Chip size="small" label={labelFor(DOCUMENT_CATEGORIES, p.value)} variant="outlined" /> },
    { field: 'hospitalName', headerName: 'Hospital', flex: 1, minWidth: 130, renderCell: (p) => p.value || '—' },
    { field: 'documentDate', headerName: 'Date', width: 120 },
    { field: 'expiryDate', headerName: 'Expiry', width: 120, renderCell: (p) => p.value ? <Chip size="small" color="warning" label={p.value} /> : '—' },
    { field: 'version', headerName: 'Ver', width: 70, renderCell: (p) => `v${p.value ?? 1}` },
    {
      field: 'actions', headerName: 'Actions', width: 170, sortable: false,
      renderCell: (p) => {
        const url = toAbsoluteFileUrl(p.row.fileUrl);
        return (
          <Box>
            {url && <Tooltip title="View"><IconButton size="small" href={url} target="_blank" rel="noopener noreferrer"><ViewIcon fontSize="small" /></IconButton></Tooltip>}
            <Tooltip title="Versions"><IconButton size="small" onClick={() => openVersions(p.row)}><HistoryIcon fontSize="small" /></IconButton></Tooltip>
            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDelId(p.row.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Medical Documents" subtitle="Repository of your medical documents" icon={<DocIcon />}
      actionLabel={officerId ? 'Upload Document' : undefined} actionIcon={<UploadIcon />} onAction={officerId ? () => setOpen(true) : undefined}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search title / hospital / doctor" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Category</InputLabel>
          <Select value={catFilter} label="Category" onChange={(e) => setCatFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {DOCUMENT_CATEGORIES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ height: 520, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={filtered} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>
      </>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Medical Document</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Document Title" required value={form.documentTitle} onChange={(e) => setForm({ ...form, documentTitle: e.target.value })} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={form.documentCategory} onChange={(e) => setForm({ ...form, documentCategory: e.target.value })}>
                {DOCUMENT_CATEGORIES.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Hospital" value={form.hospitalName} onChange={(e) => setForm({ ...form, hospitalName: e.target.value })} fullWidth />
              <TextField label="Doctor" value={form.doctorName} onChange={(e) => setForm({ ...form, doctorName: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField type="date" label="Document Date" InputLabelProps={{ shrink: true }} value={form.documentDate} onChange={(e) => setForm({ ...form, documentDate: e.target.value })} fullWidth />
              <TextField type="date" label="Expiry Date (optional)" InputLabelProps={{ shrink: true }} value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} fullWidth />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Access Level</InputLabel>
              <Select label="Access Level" value={form.accessLevel} onChange={(e) => setForm({ ...form, accessLevel: e.target.value })}>
                {ACCESS_LEVELS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} fullWidth />
            <TextField label="Description" multiline minRows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth />
            <FileUpload onFileSelected={setFile} uploading={saving} label="Select document (PDF / image)" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!officerId || !file || !form.documentTitle || saving}
            sx={{ textTransform: 'none', fontWeight: 600 }}>{saving ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={verOpen} onClose={() => setVerOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>Version History — {detail?.documentTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>Add New Version</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Version title (optional)" value={verTitle} onChange={(e) => setVerTitle(e.target.value)} sx={{ flex: 1 }} />
                <Button variant="outlined" component="label" startIcon={<UploadIcon />}>File<input type="file" hidden onChange={(e) => setVerFile(e.target.files?.[0] || null)} /></Button>
                <Button variant="contained" onClick={handleAddVersion} disabled={!verFile || uploading}>{uploading ? '...' : 'Add'}</Button>
              </Stack>
            </Box>
            <Divider />
            <Stack spacing={1}>
              {versions.map((v) => {
                const url = toAbsoluteFileUrl(v.fileUrl);
                return (
                  <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2">v{v.version} — {v.documentTitle}</Typography>
                      <Typography variant="caption" color="text.secondary">{v.uploadedAt} · {v.uploadedByName}</Typography>
                    </Box>
                    {url && <Button size="small" href={url} target="_blank" rel="noopener noreferrer">View</Button>}
                  </Box>
                );
              })}
              {versions.length === 0 && <Typography variant="body2" color="text.secondary">No previous versions.</Typography>}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={() => setVerOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <ConfirmDialog
        open={delId != null}
        title="Delete medical document?"
        message="This will permanently remove the document and its file. This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onClose={() => setDelId(null)}
      />
    </PageScaffold>
  );
};

export default DocumentsPage;